import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAppContext } from 'src/components/app-context';
import { Panel, PanelTitle } from '@prisjakt/ui/panel';
import { Maybe, Product } from 'src/types';
import { InternalLink } from 'src/ui/link';
import {
  CaptionText,
  FontColor,
  TitleMediumText,
} from '@prisjakt/ui/text/variants';
import AlertButton from 'src/components/new-product-releases/components/alert-button';
import { CallToActionButton, FlexColumn, StyledPanelHeader } from './styles';
import { ProductGridCard } from '../product-card-new';
import { ProductCardHorizontalScroll } from '../horizontal-scroll/variants/product-card-horizontal-scroll';

// This is done so that regular products and deal products
// can both be used by this component. For deal products
// you will need to do .map(deal => { ...deal.product, price: deal.price })
type SectionProduct = Omit<
  Product,
  'classicFullPath' | 'conditions' | 'category' | 'media' | 'brand'
> & {
  price?: { diffPercentage?: number };
  category?: Maybe<Pick<NonNullable<Product['category']>, 'name'>>;
  media: Pick<Product['media'], 'first'>;
  brand?: Maybe<Pick<NonNullable<Product['brand']>, 'name' | 'logo'>>;
};

type ProductsSectionProps = {
  className?: string;
  products: SectionProduct[];
  title: string;
  subTitle?: string;
  linkTitle?: string;
  linkPath?: string;
  shouldLazyLoadImages?: boolean;
  dataTestType?: string;
  ssrExpanded?: boolean;
  showCreateAlertButton?: boolean;
  showLinkAsButton?: boolean;
  onHeaderLinkClick?: (data: { link: string }) => void;
  onProductCardClick?: (data: { index: number; link: string }) => void;
  negativeMargin?: boolean;
  removeProductsWithNoImage?: boolean;
  productsInCarousel?: number;
};

const HeaderLink = ({
  linkTitle,
  linkPath,
  showLinkAsButton,
  onHeaderLinkClick,
}: Pick<
  ProductsSectionProps,
  'linkTitle' | 'linkPath' | 'showLinkAsButton' | 'onHeaderLinkClick'
>) => {
  if (!linkTitle || !linkPath) return null;

  return showLinkAsButton ? (
    <CallToActionButton component={<Link to={linkPath} />}>
      {linkTitle}
    </CallToActionButton>
  ) : (
    <InternalLink
      to={linkPath}
      onClick={() => {
        onHeaderLinkClick?.({ link: linkPath });
      }}
    >
      <TitleMediumText color={FontColor.PRIMARY}>{linkTitle}</TitleMediumText>
    </InternalLink>
  );
};

export const ProductsSection: React.FC<ProductsSectionProps> = props => {
  const {
    products,
    title,
    subTitle = null,
    shouldLazyLoadImages = true,
    dataTestType,
    showCreateAlertButton,
    onProductCardClick,
    negativeMargin,
    removeProductsWithNoImage = true,
    productsInCarousel = 12,
  } = props;
  const { t } = useAppContext();

  const productCards = useMemo(() => {
    return products
      .filter(p => {
        if (!removeProductsWithNoImage) return p;
        return p.media.first !== undefined;
      })
      .map((product, index) => (
        <ProductGridCard
          key={product.id}
          diffPercentage={product.price?.diffPercentage}
          brand={
            product.brand && {
              logo: product.brand.logo,
              name: product.brand.name,
            }
          }
          category={
            product.category && {
              name: product.category.name,
            }
          }
          imageSrc={product.media.first}
          shouldLazyLoadImage={shouldLazyLoadImages}
          link={product.pathName}
          name={product.name}
          price={
            product.priceSummary.regular || product.priceSummary.alternative
          }
          onClick={() => {
            onProductCardClick?.({ index, link: product.pathName });
          }}
          rating={
            product.aggregatedRating?.score || product.userReviewSummary?.rating
          }
          reviewsCount={
            product.aggregatedRating?.count || product.userReviewSummary?.count
          }
          background
          FooterArea={
            showCreateAlertButton ? (
              <AlertButton
                product={product}
                text={t('product', 'alert.create-button')}
                type="PRICE_DROPS"
              />
            ) : null
          }
        />
      ))
      .slice(0, productsInCarousel);
  }, [
    onProductCardClick,
    products,
    productsInCarousel,
    removeProductsWithNoImage,
    shouldLazyLoadImages,
    showCreateAlertButton,
    t,
  ]);

  if (!products?.length) return null;

  return (
    <Panel
      className={props.className}
      containerProps={
        dataTestType
          ? {
              'data-test': 'Panel',
              'data-test-type': dataTestType,
            }
          : ''
      }
    >
      <StyledPanelHeader>
        <FlexColumn>
          <PanelTitle>{title}</PanelTitle>
          {subTitle && <CaptionText>{subTitle}</CaptionText>}
        </FlexColumn>
        <HeaderLink {...props} />
      </StyledPanelHeader>
      <div>
        <ProductCardHorizontalScroll
          loadingHeight={140}
          negativeMargin={negativeMargin}
        >
          {productCards}
        </ProductCardHorizontalScroll>
      </div>
    </Panel>
  );
};
